import { useEffect, useState, type ReactNode } from "react";
import { useCustom, useCustomMutation, useOne, usePermissions, useTranslate } from "@refinedev/core";
import {
  ActionBar,
  BookIcon,
  BoxIcon,
  Button,
  CalendarIcon,
  CameraIcon,
  DropletIcon,
  FileTextIcon,
  FormCard,
  HomeIcon,
  EyeIcon,
  InfoIcon,
  LockIcon,
  MicrochipIcon,
  ScaleIcon,
  ShieldIcon,
  Modal,
  SectionHeader,
  StatusBadge,
  Tabs,
  Tile,
  TileBoard,
  UnlockIcon,
  type TileBoardItem,
} from "@fawkes/ui";

import {
  WeightsTile,
  FeedingTile, VoedingTegelKop, GewichtTegelKop,
  VaccinationsTile,
  MedicijnenTile,
  NotesTile,
  MediaTile,
  VerblijvenTile,
  ChipRegistrationTile,
  IntakeInTile,
  IntakeUitTile,
  PleeggezinTile,
  HistoryTab,
  BasisTile,
  type AnimalBasis,
} from "./animalTiles";
import { LogboekTile } from "./LogboekTile";
import { TegelIndelingDialog, type TegelKeuze } from "./TegelIndelingDialog";
import { TakenTile } from "./TakenTile";
import { WizardsTile } from "./WizardsTile";
import { NazorgVerblijfTile } from "./NazorgTile";
import { AdvertentieDetail } from "./AdvertentieDetail";
import { useModuleAvailability } from "../api/modules";
import {
  TegelInstellingenDialog,
  useAnimalFieldPreferences,
  type FieldOption,
} from "./animalTileConfig";

type TabKey = "algemeen" | "verblijf" | "advertentie" | "geschiedenis";

// The Algemeen-tab tiles, in their default order. "basis" is pinned (always visible). The order +
// hidden set are a per-user layout preference persisted server-side (the Jmix AnimalTilePreferenceService /
// `user_animal_tile_preference` table), so the layout follows the user across browsers and devices —
// shared byte-for-byte with the legacy Jmix screen via the canonical tile-key vocabulary below.
const TILE_KEYS = ["basis", "bescherming", "taken", "wizards", "medicijnen", "gewichten", "voeding", "logboek", "notities", "media"] as const;
const PINNED_TILE = "basis";

/**
 * De tegels die de zichtbaarheidsdialoog aanbiedt, in de volgorde van de bron
 * (AnimalDetailView#TILE_VISIBILITY_LABELS). Basis staat er bewust niet tussen: die blijft altijd
 * staan. Logboek is er hier achteraan bij gezet — die tegel bestond nog niet toen de lijst gemaakt
 * werd, en zonder vermelding kun je hem niet weg krijgen.
 */
export const ZICHTBAARHEID_TEGELS = [
  "bescherming",
  "taken",
  "wizards",
  "medicijnen",
  "gewichten",
  "voeding",
  "notities",
  "media",
  "logboek",
] as const;

// React board key ↔ canonical Jmix tile key (the `user_animal_tile_preference.tile_key` vocabulary).
const SERVER_KEY: Record<string, string> = {
  basis: "BASIS",
  bescherming: "INENTINGEN",
  taken: "TAKEN",
  wizards: "WIZARDS",
  medicijnen: "MEDICIJNEN",
  gewichten: "GEWICHTEN",
  voeding: "VOEDING",
  // LOGBOEK is een React-eigen tegel: Jmix kent hem niet. De sleutel staat wel in dezelfde tabel,
  // want de lay-out is per gebruiker en niet per app — wie hem hier verplaatst hoort hem daar terug
  // te vinden zodra het scherm bestaat.
  logboek: "LOGBOEK",
  notities: "NOTES",
  media: "MEDIA",
};
const REACT_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(SERVER_KEY).map(([reactKey, serverKey]) => [serverKey, reactKey]),
);

export interface TileLayout {
  order: string[];
  hidden: string[];
}

export interface TilePreference {
  tileKey: string;
  visible: boolean;
  displayOrder: number;
}

function reconcileOrder(order: string[]): string[] {
  const known = order.filter((k) => (TILE_KEYS as readonly string[]).includes(k));
  const missing = TILE_KEYS.filter((k) => !known.includes(k));
  return [...known, ...missing];
}

const DEFAULT_LAYOUT: TileLayout = { order: [...TILE_KEYS], hidden: [] };

// The per-user field-preference catalog per configurable tile (the Jmix pencil dialogs). Keys are
// namespaced `{tile}.{field}`; the bare part mirrors the Jmix AnimalCardDefault.FIELD_* / SECTION_*
// constants. Row ORDER and the label texts mirror the Jmix BASIS_FIELD_LABELS map — the order both
// Jmix dialogs (pencil + slotje) list the fields in (visual-parity pass 2026-07-04).
const BASIS_FIELD_KEYS = [
  { key: "basis.naam", labelKey: "animalDetailView.field.naam" },
  { key: "basis.geslacht", labelKey: "animalDetailView.field.geslacht" },
  { key: "basis.gecastreerd", labelKey: "animalDetailView.fieldConfig.gecastreerd" },
  { key: "basis.castratieDatum", labelKey: "animalDetailView.fieldConfig.castratieDatum" },
  { key: "basis.diersoort", labelKey: "animalDetailView.field.diersoort" },
  { key: "basis.chipPasnummer", labelKey: "animalDetailView.fieldConfig.chipPasnummer" },
  { key: "basis.paspoortNummer", labelKey: "animalDetailView.fieldConfig.paspoortNummer" },
  { key: "basis.stamboomNummer", labelKey: "animalDetailView.field.stamboom" },
  { key: "basis.geboortedatum", labelKey: "animalDetailView.field.geboortedatum" },
  { key: "basis.overledendatum", labelKey: "animalDetailView.field.overledendatum" },
  { key: "basis.dierVacht", labelKey: "animalDetailView.field.ras" },
  { key: "basis.kruising", labelKey: "animalDetailView.field.kruising" },
  { key: "basis.kenmerken", labelKey: "animalDetailView.field.kenmerken" },
  { key: "basis.foto", labelKey: "animalDetailView.fieldConfig.foto" },
] as const;
const VOEDING_SECTION_KEYS = [
  { key: "voeding.schema", labelKey: "animalDetailView.fieldConfig.voedingSchema" },
  { key: "voeding.log", labelKey: "animalDetailView.fieldConfig.voedingLog" },
] as const;
const GEWICHTEN_FIELD_KEYS = [
  { key: "gewichten.chart", labelKey: "animalDetailView.fieldConfig.gewichtenChart" },
] as const;

// Which board tiles carry the foster lock button: the policy vocabulary of FosterTilePolicyService.
// WIZARDS is tile-level-only (no field rows; ceiling EDITABLE, default HIDDEN) — the generic
// FosterVisibilityDialog omits the field list when fieldLabels is empty.
const FOSTER_TILES: ReadonlySet<string> = new Set(["basis", "bescherming", "taken", "wizards", "gewichten", "voeding", "notities", "media"]);
// The Jmix ROLE_FOSTER_POLICY_EDIT authority gating GET/PUT /api/foster-policy and the lock buttons.
const FOSTER_POLICY_AUTHORITY = "ROLE_FOSTER_POLICY_EDIT";

type FieldDialogKey = "basis" | "voeding" | "gewichten";

// Map the server's per-tile {tileKey, visible, displayOrder} rows (all tiles, incl. ones from other
// tabs) onto this board's order + hidden set. Only board tiles are kept; order follows displayOrder;
// the pinned Basis tile can never be hidden.
export function serverToLayout(prefs: TilePreference[]): TileLayout {
  const known = prefs
    .filter((p) => REACT_KEY[p.tileKey] !== undefined)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const order = reconcileOrder(known.map((p) => REACT_KEY[p.tileKey]));
  const hidden = known
    .filter((p) => !p.visible && REACT_KEY[p.tileKey] !== PINNED_TILE)
    .map((p) => REACT_KEY[p.tileKey]);
  return { order, hidden };
}

// Build the full PUT payload for this board's tiles: renumber displayOrder in steps of 10 (matching
// Jmix), and derive visibility from the hidden set (Basis stays visible).
export function layoutToPreferences(layout: TileLayout): TilePreference[] {
  const hiddenSet = new Set(layout.hidden);
  return layout.order.map((reactKey, i) => ({
    tileKey: SERVER_KEY[reactKey],
    visible: reactKey === PINNED_TILE ? true : !hiddenSet.has(reactKey),
    displayOrder: (i + 1) * 10,
  }));
}

// Animal-detail migration (Jmix-exit): the React replacement for the Jmix `AnimalDetailView`
// (route animals/:id). Reproduces the tab strip (Algemeen / Verblijf / Geschiedenis) and the Algemeen
// tile grid via the reusable @fawkes/ui Tile + TileBoard. A lock/unlock toggle enters "layout edit
// mode" (the Jmix layout-lock affordance): only then can tiles be re-ordered (drag) or shown/hidden;
// the layout persists server-side per user. The Basis tile is pinned.
export function AnimalDetail({ id, onBack, onOpenAnimal, onStartInboeken, onEditInboeken, onStartUitboeken, onOpenRelation, onOpenFactuur, onOpenNazorg, openVaccinationId, initialTab }: { id: string; onBack: () => void; onOpenAnimal?: (id: string) => void; onStartInboeken?: (id: string) => void; onEditInboeken?: (verblijfId: string) => void; onStartUitboeken?: (id: string) => void; onOpenRelation?: (klantId: number) => void; onOpenFactuur?: (factuurId: number) => void; /** "Bekijk dossier" op de nazorgtegel: het nazorgdossier van die inzending. */ onOpenNazorg?: (inzendingId: string) => void; openVaccinationId?: number | null; /** Open straight on a tab — a stay link from the relation lands on Verblijf, like Jmix. */ initialTab?: TabKey }) {
  const t = useTranslate();
  // Advertentie tab gating (Jmix parity: AnimalDetailView gates the tab on
  // moduleAvailability.isEnabled("advertentie")). The advertentie management itself lives in the
  // dedicated React advertenties module; this tab embeds that animal's single ad read/edit aggregate.
  const { isModuleEnabled } = useModuleAvailability();
  const advertentieOn = isModuleEnabled("advertentie");
  // Taken tile (Epic T5, Jmix parity): the Jmix AnimalDetailView only registers the taken tile while
  // the takenmodule is on. TileBoard ignores order keys without a matching item, so the persisted
  // per-user layout (which may contain TAKEN) stays valid while the module is off.
  const takenOn = isModuleEnabled("taken");
  // Wizards tile (WIZARDS migration, Jmix parity): AnimalDetailView registers tileWizards only while
  // moduleAvailability.isEnabled("wizards") — same pattern as the taken tile.
  const wizardsOn = isModuleEnabled("wizards");
  // Nazorg tile (Track B, Jmix parity: AnimalStayTabFragment gates the VERBLIJF_NAZORG tile on
  // moduleAvailability.isEnabled("nazorg")); the post-departure gate lives inside the tile itself.
  const nazorgOn = isModuleEnabled("nazorg");
  // A deep-link from the Entingen-/Ontwormen-overzicht "Bewerken" action lands on the Algemeen tab (the
  // Bescherming tile lives there) and asks the tile to open its edit dialog on the given inenting.
  const [tab, setTab] = useState<TabKey>(initialTab ?? "algemeen");
  // Jmix parity (AnimalStayTabFragment.selectedOverviewVerblijf): clicking a card in the Verblijven-
  // overzicht selects that stay, and the IN/UIT tiles then render it instead of the current stay.
  const [selectedStayId, setSelectedStayId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [zichtbaarheidOpen, setZichtbaarheidOpen] = useState(false);
  const [layout, setLayout] = useState<TileLayout>(DEFAULT_LAYOUT);

  const { data: prefData, refetch: refetchPrefs } = useCustom({
    url: "animals/tile-preferences",
    method: "get",
  });
  const prefs = prefData?.data as TilePreference[] | undefined;
  const { mutate: savePrefs } = useCustomMutation();

  // Per-user field visibility inside the tiles (the pencil dialogs) + the org-wide foster policy
  // (the lock dialogs, ROLE_FOSTER_POLICY_EDIT only).
  const { hidden: hiddenFields, refetch: refetchFields } = useAnimalFieldPreferences();
  const { data: permissions } = usePermissions<string[]>();
  const canFosterPolicy = (permissions ?? []).includes(FOSTER_POLICY_AUTHORITY);
  const [instellingenTegel, setInstellingenTegel] = useState<{ key: string; pencil: FieldDialogKey | null } | null>(null);

  // Seed the layout from the server once it arrives, and re-seed after every persist (the save's
  // onSuccess refetches), so the board reflects the stored per-user layout.
  useEffect(() => {
    if (prefs) setLayout(serverToLayout(prefs));
  }, [prefs]);

  const persist = (next: TileLayout) => {
    setLayout(next);
    savePrefs(
      {
        url: "animals/tile-preferences",
        method: "put",
        values: { preferences: layoutToPreferences(next) },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => refetchPrefs(), onError: () => refetchPrefs() },
    );
  };

  const { data, isLoading, isError } = useOne<AnimalBasis>({ resource: "animals", id });
  const a = data?.data;

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError || !a) return <p className="fawkes-error">{t("animalDetailView.loadFailed")}</p>;

  const tabs = [
    { key: "algemeen", label: t("animalDetailView.tab.general") },
    { key: "verblijf", label: t("animalDetailView.tab.verblijf") },
    ...(advertentieOn ? [{ key: "advertentie", label: t("animalDetailView.tab.advertentie") }] : []),
    { key: "geschiedenis", label: t("animalDetailView.tab.geschiedenis") },
  ];

  // Bare hidden-key views per configurable tile (the tiles take unprefixed keys).
  const basisHidden = new Set(
    BASIS_FIELD_KEYS.filter((f) => hiddenFields.has(f.key)).map((f) => f.key.substring("basis.".length)),
  );
  const voedingHidden = new Set(
    VOEDING_SECTION_KEYS.filter((f) => hiddenFields.has(f.key)).map((f) => f.key.substring("voeding.".length)),
  );
  const chartHidden = hiddenFields.has("gewichten.chart");

  // The Jmix tile-header affordances: a pencil for the tiles with per-user configurable fields, a
  // lock (foster policy) for the tiles in the policy vocabulary. Rendered at the top of the tile
  // content (the shared board chrome has no per-tile action slot). Jmix parity (verified against
  // the running :8080 app): the buttons appear ONLY while layout-edit mode is active.
  const withConfig = (reactKey: string, pencil: FieldDialogKey | null, content: ReactNode) => {
    void reactKey;
    void pencil;
    return content;
  };
  // De puntjes rechtsboven (alleen in de bewerkstand) openen het instellingenvenster van de tegel: welke
  // velden je in de backoffice ziet en wat het pleeggezin mag. Tegels zonder instellingen houden gewone puntjes.
  const instellingen = (reactKey: string, pencil: FieldDialogKey | null) => {
    const lock = canFosterPolicy && FOSTER_TILES.has(reactKey);
    if (!pencil && !lock) return {};
    return {
      onHandleClick: () => setInstellingenTegel({ key: reactKey, pencil }),
      handleLabel: t("animalDetailView.tileSettings.open"),
    };
  };

  const tiles: TileBoardItem[] = [
    { key: "basis", title: t("animalDetailView.tile.basis"), ...instellingen("basis", "basis"), content: withConfig("basis", "basis", <BasisTile animalId={id} onOpenAnimal={onOpenAnimal} hiddenFields={basisHidden} />), pinned: true },
    { key: "bescherming", title: t("animalDetailView.tile.bescherming"), ...instellingen("bescherming", null), content: withConfig("bescherming", null, <VaccinationsTile animalId={id} openInentingId={openVaccinationId ?? null} />) },
    ...(takenOn ? [{ key: "taken", title: t("animalDetailView.tile.taken"), ...instellingen("taken", null), content: withConfig("taken", null, <TakenTile animalId={id} />) }] : []),
    ...(wizardsOn ? [{ key: "wizards", title: t("animalDetailView.tile.wizards"), ...instellingen("wizards", null), content: withConfig("wizards", null, <WizardsTile animalId={id} />) }] : []),
    { key: "medicijnen", title: t("animalDetailView.tile.medicijnen"), content: <MedicijnenTile animalId={id} /> },
    { key: "gewichten", title: <GewichtTegelKop titel={t("animalDetailView.tile.gewichten")} />, ...instellingen("gewichten", "gewichten"), content: withConfig("gewichten", "gewichten", <WeightsTile animalId={id} chartHidden={chartHidden} />) },
    { key: "voeding", title: <VoedingTegelKop titel={t("animalDetailView.tile.voeding")} onder={t("animalDetailView.feeding.subtitle")} />, ...instellingen("voeding", "voeding"), content: withConfig("voeding", "voeding", <FeedingTile animalId={id} hiddenSections={voedingHidden} />) },
    { key: "logboek", title: t("animalDetailView.tile.logboek"), ...instellingen("logboek", null), content: withConfig("logboek", null, <LogboekTile animalId={id} />) },
    { key: "notities", title: t("animalDetailView.tile.notities"), ...instellingen("notities", null), content: withConfig("notities", null, <NotesTile animalId={id} />) },
    { key: "media", title: t("animalDetailView.tile.media"), ...instellingen("media", null), content: withConfig("media", null, <MediaTile animalId={id} />) },
  ];

  // The pencil-dialog catalogs, with translated labels.
  const fieldDialogFields: Record<FieldDialogKey, FieldOption[]> = {
    basis: BASIS_FIELD_KEYS.map((f) => ({ key: f.key, label: t(f.labelKey) })),
    voeding: VOEDING_SECTION_KEYS.map((f) => ({ key: f.key, label: t(f.labelKey) })),
    gewichten: GEWICHTEN_FIELD_KEYS.map((f) => ({ key: f.key, label: t(f.labelKey) })),
  };

  // Jmix shows a lock glyph while the layout is locked (click to edit) and an unlock glyph while
  // editing (click to finish); the titles reuse the established edit/done labels.
  const layoutToggleTitle = editing
    ? t("animalDetailView.layout.done")
    : t("animalDetailView.layout.edit");

  return (
    <FormCard>
      <SectionHeader>
        <span className="fawkes-animal-detail__heading">
          {/* Het dierregistratienummer staat voor de naam: daarmee noemt de opvang het dier op de
              kooikaart, aan de telefoon en op elk formulier. */}
          {a.registratieNummer != null && (
            <span className="fawkes-animal-detail__registratienummer">{a.registratieNummer}</span>
          )}
          {a.naam}
          {a.overleden && <StatusBadge tone="neutral">{t("animalDetailView.status.overleden")}</StatusBadge>}
        </span>
      </SectionHeader>

      <div className="fawkes-animal-detail__tabsrow">
        <Tabs tabs={tabs} activeKey={tab} onChange={(k) => setTab(k as TabKey)} ariaLabel={t("animalDetailView.title")} />
        {tab === "algemeen" && (
          <Button
            variant="secondary"
            className={`fawkes-animal-detail__layout-toggle${editing ? " fawkes-animal-detail__layout-toggle--active" : ""}`}
            aria-label={layoutToggleTitle}
            aria-pressed={editing}
            title={layoutToggleTitle}
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? <UnlockIcon width={18} height={18} /> : <LockIcon width={18} height={18} />}
          </Button>
        )}
        {/* Het oog verschijnt pas als de indeling ontgrendeld is, precies als in de bron: daar
            regelt één dialoog welke tegels je ziet, niet een knopje op elke tegel. */}
        {tab === "algemeen" && editing && (
          <Button
            variant="secondary"
            className="fawkes-animal-detail__layout-toggle"
            aria-label={t("animalDetailView.tileVisibility.open")}
            title={t("animalDetailView.tileVisibility.open")}
            onClick={() => setZichtbaarheidOpen(true)}
          >
            <EyeIcon width={18} height={18} />
          </Button>
        )}
      </div>

      {zichtbaarheidOpen && (
        <TegelIndelingDialog
          tegels={tiles.map((tile) => tegelKeuze(tile.key, t))}
          indeling={layout}
          standaardVolgorde={TILE_KEYS}
          categorieen={TEGEL_CATEGORIEEN.map((c) => ({ key: c, label: t(`animalDetailView.tileVisibility.cat.${c}`) }))}
          onClose={() => setZichtbaarheidOpen(false)}
          onSave={(nieuw) => {
            persist(nieuw);
            setZichtbaarheidOpen(false);
          }}
          t={t}
        />
      )}

      {tab === "algemeen" && (
        <div role="tabpanel" aria-labelledby="fui-tab-algemeen">
          <TileBoard
            items={tiles}
            order={layout.order}
            hidden={layout.hidden}
            editing={editing}
            showHideControls={false}
            onReorder={(order) => persist({ ...layout, order })}
            onToggleHidden={(key, hidden) =>
              persist({
                ...layout,
                hidden: hidden ? [...layout.hidden, key] : layout.hidden.filter((k) => k !== key),
              })
            }
            labels={{
              hide: t("animalDetailView.layout.hide"),
              drag: t("animalDetailView.layout.drag"),
              hiddenSection: t("animalDetailView.layout.hiddenSection"),
            }}
          />
        </div>
      )}

      {/* Source layout (Jmix stay tab): tiles flow into a responsive auto-fill grid (2 columns ~1400px,
          3 columns on wide screens) — Verblijven-overzicht, then the IN / UIT / Pleeggezin / Chip /
          Nazorg detail tiles. */}
      {tab === "verblijf" && (
        <div role="tabpanel" aria-labelledby="fui-tab-verblijf" className="fawkes-animal-detail__tab-tiles">
          <Tile title={t("animalDetailView.stay.tile.overzicht")}>
            <VerblijvenTile
              animalId={id}
              selectedStayId={selectedStayId}
              onSelectStay={(stayId) => setSelectedStayId((prev) => (prev === stayId ? null : stayId))}
            />
          </Tile>
          {/* No wrapper title: acc's IN card has no "IN" header — the tile renders its own "INTAKE"
              header (icon + label + potlood) exactly like acc. A titled Tile would double it up. */}
          <section className="fui-tile fawkes-stay-tile-card">
            <IntakeInTile animalId={id} selectedStayId={selectedStayId} onStartInboeken={onStartInboeken} onEditInboeken={onEditInboeken} onOpenRelation={onOpenRelation} onOpenAnimal={onOpenAnimal} onOpenFactuur={onOpenFactuur} />
          </section>
          {/* acc keeps the "UIT" title + Uitboeken action while a stay is still open (placeholder
              "Nog geen vertrek geregistreerd"), so the wrapper title stays for the UIT card. */}
          {/* Like the IN card: the tile draws its own header (glyph + "UIT" + the action), so the
              wrapper carries no title — a titled Tile would print "UIT" twice. */}
          <section className="fui-tile fawkes-stay-tile-card">
            <IntakeUitTile animalId={id} selectedStayId={selectedStayId} onStartUitboeken={onStartUitboeken} onOpenRelation={onOpenRelation} onOpenFactuur={onOpenFactuur} />
          </section>
          <Tile
            title={
              <span className="fawkes-pg-tile-title">
                <HomeIcon className="fawkes-pg-tile-title__icon" width={18} height={18} aria-hidden="true" />
                {t("animalDetailView.stay.tile.pleeggezin")}
              </span>
            }
          >
            <PleeggezinTile animalId={id} selectedStayId={selectedStayId} onOpenRelation={onOpenRelation} />
          </Tile>
          {/* PetBase chip registration renders its own Tile chrome and self-hides when the animal has
              no registration yet — an empty "Nog niets vastgelegd" card was pure noise on the tab. */}
          <ChipRegistrationTile animalId={id} />
          {/* Jmix VERBLIJF_NAZORG (default order 50); renders its own Tile chrome because it self-hides
              for animals without a completed stay. */}
          {nazorgOn && (
            <NazorgVerblijfTile animalId={id} selectedStayId={selectedStayId} onOpenInzending={onOpenNazorg} />
          )}
        </div>
      )}

      {tab === "advertentie" && advertentieOn && (
        <div role="tabpanel" aria-labelledby="fui-tab-advertentie">
          <AnimalAdvertentieTab animalId={id} onBack={() => setTab("algemeen")} />
        </div>
      )}

      {tab === "geschiedenis" && (
        <div role="tabpanel" aria-labelledby="fui-tab-geschiedenis">
          <Tile title={t("animalDetailView.tab.geschiedenis")}>
            <HistoryTab animalId={id} />
          </Tile>
        </div>
      )}

      {instellingenTegel && (
        <TegelInstellingenDialog
          tileKey={SERVER_KEY[instellingenTegel.key]}
          tileLabel={t(`animalDetailView.tile.${instellingenTegel.key}`)}
          fields={instellingenTegel.pencil ? fieldDialogFields[instellingenTegel.pencil] : []}
          hiddenPrefs={hiddenFields}
          fosterAllowed={canFosterPolicy && FOSTER_TILES.has(instellingenTegel.key)}
          backofficeTileVisible={!layout.hidden.includes(instellingenTegel.key)}
          onClose={() => setInstellingenTegel(null)}
          onSaved={() => refetchFields()}
        />
      )}

      <ActionBar>
        <Button variant="secondary" onClick={onBack}>
          {t("common.back")}
        </Button>
      </ActionBar>
    </FormCard>
  );
}

// The animal's Advertentie tab — embeds the (at most one) advertisement's read/edit aggregate from the
// dedicated advertentie module, keyed by the animal's single ad (GET /api/herplaatsing/by-dier/{id},
// 204 when none). Mirrors the Jmix AnimalDetailView advertentie tab (AdvertentiePanel.loadForDier).
function AnimalAdvertentieTab({ animalId, onBack }: { animalId: string; onBack: () => void }) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<{ id: string }>({
    url: `herplaatsing/by-dier/${animalId}`,
    method: "get",
    // A transient backend hiccup (e.g. the DB briefly recovering) otherwise caches an empty/failed
    // result that sticks until a full page reload — and React Query caches per browser, so one tab can
    // keep showing the error while another loads fine. Auto-retry and always re-fetch on (re)mount so
    // the tab self-heals instead of relying on the manual "Opnieuw proberen" button.
    queryOptions: { retry: 2, refetchOnMount: "always", staleTime: 0 },
  });
  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  const adId: string | null = data?.data?.id ?? null;
  if (isError || !adId) {
    // getOrCreateForDier always returns an advertisement, so a missing id means the request failed
    // (e.g. a transient backend hiccup) — offer a reload instead of the misleading "no ad" placeholder,
    // which would otherwise stick as a cached empty result until a full page reload.
    return (
      <Tile title={t("animalDetailView.tab.advertentie")}>
        <p className="fawkes-tile-placeholder">{t("animalDetailView.advertentie.laadFout")}</p>
        <Button variant="secondary" onClick={() => refetch()}>{t("common.opnieuw")}</Button>
      </Tile>
    );
  }
  return <AdvertentieDetail id={adId} onBack={onBack} />;
}

/**
 * "Tegels tonen of verbergen" — de dialoog achter het oog. Eén lijst met vinkjes, in de volgorde van
 * de bron, en pas op Opslaan verandert er iets. Zo zie je in één keer wat er allemaal is in plaats
 * van tegel voor tegel te moeten wegklikken.
 */
/**
 * Wat er verborgen is nadat je de dialoog opslaat: de vinkjes bepalen alleen de tegels die in de
 * lijst staan. Wat er niet in staat — Basis, en tegels die door een instelling helemaal uit staan —
 * houdt de stand die het had, anders zou één keer opslaan die stilletjes terugzetten.
 */
export function verborgenNa(
  hidden: string[],
  keuzes: readonly string[],
  zichtbaar: Record<string, boolean>,
): string[] {
  const buiten = hidden.filter((k) => !keuzes.includes(k));
  return [...buiten, ...keuzes.filter((k) => zichtbaar[k] === false)];
}

/** Categorieën in de dialoog "Tegels tonen of verbergen", in de volgorde van het filtermenu. */
const TEGEL_CATEGORIEEN = ["algemeen", "medisch", "verzorging", "registratie", "documentatie"] as const;

/** Icoon, tint, categorie en omschrijving per tegel van de dierpagina. */
const TEGEL_INFO: Record<string, { icoon: ReactNode; tint: TegelKeuze["tint"]; categorie: (typeof TEGEL_CATEGORIEEN)[number] }> = {
  basis: { icoon: <InfoIcon width={20} height={20} />, tint: "groen", categorie: "algemeen" },
  bescherming: { icoon: <ShieldIcon width={20} height={20} />, tint: "accent", categorie: "medisch" },
  taken: { icoon: <CalendarIcon width={20} height={20} />, tint: "oranje", categorie: "registratie" },
  wizards: { icoon: <MicrochipIcon width={20} height={20} />, tint: "turkoois", categorie: "registratie" },
  medicijnen: { icoon: <DropletIcon width={20} height={20} />, tint: "rood", categorie: "medisch" },
  gewichten: { icoon: <ScaleIcon width={20} height={20} />, tint: "geel", categorie: "verzorging" },
  voeding: { icoon: <BoxIcon width={20} height={20} />, tint: "accent", categorie: "verzorging" },
  logboek: { icoon: <BookIcon width={20} height={20} />, tint: "accent", categorie: "documentatie" },
  notities: { icoon: <FileTextIcon width={20} height={20} />, tint: "grijs", categorie: "documentatie" },
  media: { icoon: <CameraIcon width={20} height={20} />, tint: "paars", categorie: "documentatie" },
};

function tegelKeuze(key: string, t: (key: string) => string): TegelKeuze {
  const info = TEGEL_INFO[key] ?? { icoon: <InfoIcon width={20} height={20} />, tint: "grijs" as const, categorie: "algemeen" as const };
  return {
    key,
    titel: t(`animalDetailView.tile.${key}`),
    omschrijving: t(`animalDetailView.tileVisibility.desc.${key}`),
    icoon: info.icoon,
    tint: info.tint,
    categorie: info.categorie,
    vast: key === PINNED_TILE,
  };
}

// De oude dialoog met alleen vinkjes; vervangen door TegelIndelingDialog (tonen/verbergen én volgorde).
export function TileVisibilityDialog({ keuzes, hidden, titel, onClose, onSave, t }: {
  keuzes: readonly string[];
  hidden: string[];
  titel: (key: string) => string;
  onClose: () => void;
  onSave: (hidden: string[]) => void;
  t: (key: string) => string;
}) {
  const [zichtbaar, setZichtbaar] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(keuzes.map((k) => [k, !hidden.includes(k)])),
  );

  return (
    <Modal
      open
      title={t("animalDetailView.tileVisibility.title")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            variant="primary"
            onClick={() => onSave(verborgenNa(hidden, keuzes, zichtbaar))}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="fawkes-tile-visibility">
        {keuzes.map((k) => (
          <label className="fawkes-tile-visibility__rij" key={k}>
            <input
              type="checkbox"
              checked={zichtbaar[k] ?? true}
              onChange={(e) => setZichtbaar((vorig) => ({ ...vorig, [k]: e.target.checked }))}
            />
            <span>{titel(k)}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}
